import type { CommitmentSummary } from './types';

/**
 * Porządek i wiek wierszy panelu zobowiązań — czysta logika, bez Reacta.
 *
 * Mieszka osobno od komponentu, bo to **decyzja produktowa**, nie szczegół renderowania:
 * decyzja 66 rozstrzyga kolejność wprost, a testy mają ją pilnować bez montowania ekranu.
 */

/**
 * Kubełek stanu w rozumieniu panelu. To **nie to samo co `CommitmentState`** —
 * sześć stanów modelu zwija się w cztery grupy, które człowiek rozróżnia wzrokiem
 * (makieta E1: cztery różne znaki `⚠ ⚡ ✓ 💤`).
 *
 * ⭐ Najważniejsze zwężenie: `active` rozpada się na **dwa** kubełki. „Obudzone"
 * i „do zrobienia" to ten sam stan w bazie, a zupełnie inna informacja dla człowieka —
 * pierwsze przyszło **z cudzej roboty**, drugie leżało tu od początku.
 *
 * ⚠ **Odróżnia je surt, NIE `firedAtUtc`.** To pułapka, w którą wpadłem: w backendzie
 * `fired_at_utc` znaczy „jest aktywne teraz" (`CommitmentRows.Of` czyta jego brak jako
 * „śpi"), więc zadanie **bezwarunkowe dostaje je już przy utworzeniu** — na żywych danych
 * 514 z 654. Reguła oparta na tym znaczniku oznaczałaby ⚡ całą zwykłą listę.
 *
 * Zadanie z iteracji zostaje przy „do zrobienia", tak jak w makiecie E1: iterację
 * odpalasz sam, więc nie jest niespodzianką. Surt odpalił ktoś inny — i to jest nowa
 * informacja, o którą w decyzji 66 chodzi.
 */
export type CommitmentBucket = 'draugr' | 'fired' | 'todo' | 'sleeping' | 'closed';

/**
 * Kolejność kubełków (decyzja 66: *„draugr → obudzone → do zrobienia → śpiące"*).
 *
 * Draugr jest pierwszy, bo to **jedyny stan wymagający decyzji człowieka** — system
 * go sam nie zamknie (decyzja 21). Obudzone są drugie, bo są nową informacją.
 */
const BUCKET_ORDER: Record<CommitmentBucket, number> = {
  draugr: 0,
  fired: 1,
  todo: 2,
  sleeping: 3,
  closed: 4,
};

export function commitmentBucket(commitment: CommitmentSummary): CommitmentBucket {
  switch (commitment.state) {
    case 'orphaned':
      return 'draugr';

    case 'active':
      // Czynne i pochodzące z zapalnika = ktoś inny je obudził swoją robotą.
      return commitment.surtSourceCommitmentId === null ? 'todo' : 'fired';

    case 'dormant':
    case 'snoozed':
      return 'sleeping';

    case 'done':
    case 'obsolete':
      return 'closed';
  }
}

/**
 * Od kiedy ta pozycja **wymaga uwagi** — punkt odniesienia dla kolumny „wisi od".
 *
 * ⚠ To celowo nie jest data utworzenia dla wszystkich. Zobowiązanie obudzone wczoraj,
 * a utworzone pół roku temu, nie „wisi od pół roku" — wisiało uśpione, czyli nie wisiało
 * wcale. Liczy się moment, od którego ktoś mógł zacząć działać.
 *
 * Łańcuchy `??` są zabezpieczeniem przed niepełnymi danymi (import, stary wiersz),
 * a nie normalną ścieżką: kończą się na `createdAtUtc`, które jest zawsze.
 */
export function waitingSince(commitment: CommitmentSummary): string {
  switch (commitmentBucket(commitment)) {
    case 'draugr':
      // Draugr zaczął wymagać uwagi w chwili osierocenia, a nie obudzenia.
      return commitment.orphanedAtUtc ?? commitment.firedAtUtc ?? commitment.createdAtUtc;

    case 'closed':
      return commitment.createdAtUtc;

    default:
      // `firedAtUtc` to znacznik AKTYWACJI (patrz `commitmentBucket`), więc odpowiada
      // dokładnie na pytanie „od kiedy dało się to ruszyć". Dla zadania z iteracji
      // jest to start iteracji, dla bezwarunkowego — moment utworzenia.
      return commitment.firedAtUtc ?? commitment.createdAtUtc;
  }
}

/**
 * Porównanie dwóch wierszy: najpierw kubełek, potem wiek.
 *
 * ⚠ Rozstrzygnięcie remisu po `id` **nie jest ozdobą**. Bez niego dwie pozycje z tym
 * samym znacznikiem czasu — a import wstawia całe wsady w jednej transakcji, więc to
 * przypadek normalny — mogą zamienić się miejscami między renderami. Wiersze skaczące
 * bez powodu to dokładnie ta wada, którą decyzja 45 wytyka sortowaniu po cieple.
 */
export function compareForPanel(a: CommitmentSummary, b: CommitmentSummary): number {
  const byBucket = BUCKET_ORDER[commitmentBucket(a)] - BUCKET_ORDER[commitmentBucket(b)];
  if (byBucket !== 0) {
    return byBucket;
  }

  // Rosnąco po dacie = najstarsze na górze, czyli „najdłużej wiszące u góry".
  const byAge = waitingSince(a).localeCompare(waitingSince(b));
  if (byAge !== 0) {
    return byAge;
  }

  return a.id - b.id;
}

/** Nowa tablica — wejście zostaje nietknięte, bo bywa nim pamięć podręczna zapytania. */
export function sortForPanel(
  commitments: readonly CommitmentSummary[],
): readonly CommitmentSummary[] {
  return [...commitments].sort(compareForPanel);
}

/** Ile czasu wisi — w godzinach, jeśli krócej niż dobę, inaczej w dniach. */
export interface WaitingAge {
  unit: 'hours' | 'days';
  value: number;
}

/**
 * ⚠ Wynik jest **przycinany do zera**, a nie ujemny. Zegar przeglądarki bywa
 * przestawiony względem serwera i „wisi od −2 godzin" wygląda na awarię aplikacji,
 * choć jest awarią zegara.
 */
export function waitingAge(commitment: CommitmentSummary, now: Date): WaitingAge {
  const since = Date.parse(waitingSince(commitment));

  // Data nie do sparsowania nie ma prawa wywrócić całej listy — zero jest czytelne.
  const elapsed = Number.isNaN(since) ? 0 : Math.max(0, now.getTime() - since);

  const hours = Math.floor(elapsed / 3_600_000);

  return hours < 24 ? { unit: 'hours', value: hours } : { unit: 'days', value: Math.floor(hours / 24) };
}
